import React, { useState, useEffect } from 'react';
import { 
  Home,
  User, 
  History, 
  Lock, 
  ShieldCheck, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  IdCard, 
  Save, 
  CheckCircle2, 
  AlertCircle, 
  RotateCw, 
  LogOut,
  Package,
  ArrowRight,
  ChevronRight,
  Eye,
  EyeOff,
  Smartphone,
  MessageSquare,
  Edit3,
  X,
  QrCode,
  ExternalLink,
  Copy,
  Check,
  Bell,
  Send,
  RefreshCw,
  Sparkles,
  Terminal,
  Truck,
  Search,
  Receipt,
  ChevronDown,
  TrendingUp,
  CreditCard,
  Info,
  Rocket,
  Compass
} from 'lucide-react';
import { SalesSlipModal } from './SalesSlipModal';
import { 
  db, 
  doc, 
  secureGetDoc, 
  secureSetDoc, 
  auth, 
  signOut 
} from '../firebase';
import { 
  updatePassword, 
  updateEmail, 
  reauthenticateWithCredential, 
  EmailAuthProvider 
} from 'firebase/auth';
import { OrderRecord, DistributorConfig } from '../types';
import { formatThaiDateTime } from '../lib/dateUtils';
import { ThaiAddress } from '../data/thaiAddresses';
import { searchThaiAddress, parseThaiAddress } from '../lib/thaiAddressesDb';

interface MemberDashboardViewProps {
  memberId: string;
  currentUser: any;
  onOpenAuth: () => void;
  onLogout: () => void;
  distributor: DistributorConfig;
}

// Helper function to mask sensitive user IDs for general user view (shows only first 5 characters, masks the rest)
const maskAfterFiveChars = (val: string | null | undefined): string => {
  if (!val || val === '-' || !val.trim()) return '-';
  const str = val.trim();
  if (str.length <= 5) return str;
  return str.slice(0, 5) + '*'.repeat(Math.min(str.length - 5, 14));
};

export const MemberDashboardView: React.FC<MemberDashboardViewProps> = ({
  memberId,
  currentUser,
  onOpenAuth,
  onLogout,
  distributor
}) => {
  const [activeMenu, setActiveMenu] = useState<'profile' | 'orders' | 'password' | 'pin' | 'email'>('profile');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // 1. Profile States
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [address3, setAddress3] = useState('');
  const [lineUserId, setLineUserId] = useState('');
  const [lastDeviceId, setLastDeviceId] = useState('');

  // Backup states to revert on cancel
  const [initialFullName, setInitialFullName] = useState('');
  const [initialPhoneNumber, setInitialPhoneNumber] = useState('');
  const [initialShippingAddress, setInitialShippingAddress] = useState('');
  const [initialAddress1, setInitialAddress1] = useState('');
  const [initialAddress2, setInitialAddress2] = useState('');
  const [initialAddress3, setInitialAddress3] = useState('');

  // Autocomplete suggestions for address
  const [addressSuggestions, setAddressSuggestions] = useState<ThaiAddress[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeFocusField, setActiveFocusField] = useState<'part2' | 'part3' | null>(null);

  // Form validation tracking
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // Form field validity checkers
  const cleanPhone = phoneNumber.replace(/\D/g, '');
  const isFullNameValid = fullName.trim().length > 0;
  const isPhoneValid = cleanPhone.length >= 9 && cleanPhone.length <= 10;
  const isAddress1Valid = address1.trim().length > 0;
  const isAddress2Valid = address2.trim().length > 0;
  const isAddress3Valid = address3.trim().length === 5 && /^\d{5}$/.test(address3.trim());
  const isFormValid = isFullNameValid && isPhoneValid && isAddress1Valid && isAddress2Valid && isAddress3Valid;

  const handlePart2Change = async (val: string) => {
    setAddress2(val);
    setActiveFocusField('part2');
    if (val.trim().length >= 2) {
      const res = await searchThaiAddress(val);
      setAddressSuggestions(res);
      setShowSuggestions(true);
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handlePart3Change = async (val: string) => {
    const cleaned = val.replace(/\D/g, '').slice(0, 5);
    setAddress3(cleaned);
    setActiveFocusField('part3');
    if (cleaned.length >= 2) {
      const res = await searchThaiAddress(cleaned);
      setAddressSuggestions(res);
      setShowSuggestions(true);
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectAddress = (item: ThaiAddress) => {
    setAddress2(`ต.${item.s} อ.${item.d} จ.${item.p}`);
    setAddress3(item.z);
    setShowSuggestions(false);
  };

  // 2. Order History States
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [orderStatusFilter, setOrderStatusFilter] = useState<string>('all');
  const [orderSearchQuery, setOrderSearchQuery] = useState<string>('');
  const [selectedOrderForSlip, setSelectedOrderForSlip] = useState<OrderRecord | null>(null);

  // 3. Change Password States
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);

  // 4. Change PIN States
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');

  // 5. Change Email States
  const [currentPassForEmail, setCurrentPassForEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');

  // 6. LINE Connect Modal & Binding States
  const [isLineModalOpen, setIsLineModalOpen] = useState(false);
  const [checkingLineStatus, setCheckingLineStatus] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  const [manualLineId, setManualLineId] = useState('');
  const [savingLineBinding, setSavingLineBinding] = useState(false);
  const [sendingTestPush, setSendingTestPush] = useState(false);
  const [lineModalMsg, setLineModalMsg] = useState<{ type: 'success' | 'error'; text: string; details?: any } | null>(null);
  const [diagnosticData, setDiagnosticData] = useState<{
    status: string;
    appUrl: string;
    publishedDomain: string;
    recommendedWebhookUrls: string[];
    lineOaId: string;
    lineAddFriendUrl: string;
    environment: {
      hasChannelAccessToken: boolean;
      hasChannelSecret: boolean;
      lineOaId: string;
    };
    errors: string[];
    recentLogs: Array<{
      id: string;
      timestamp: string;
      type: string;
      message: string;
      details?: any;
    }>;
  } | null>(null);
  const [loadingDiagnostic, setLoadingDiagnostic] = useState(false);
  const [activeLineTab, setActiveLineTab] = useState<'bind' | 'diagnostic'>('bind');

  // Fetch Webhook & LINE Diagnostic Data
  const fetchDiagnostic = async () => {
    try {
      setLoadingDiagnostic(true);
      const res = await fetch('/api/line/diagnostic');
      if (res.ok) {
        const data = await res.json();
        setDiagnosticData(data);
      }
    } catch (err) {
      console.error('Failed to fetch diagnostic info:', err);
    } finally {
      setLoadingDiagnostic(false);
    }
  };

  // Sync LINE binding status from backend
  const syncLineBindingStatus = async (autoSaveToFirestore = true) => {
    if (!memberId) return false;
    try {
      setCheckingLineStatus(true);
      const res = await fetch(`/api/line/status/${encodeURIComponent(memberId)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.bound && data.lineUserId) {
          setLineUserId(data.lineUserId);
          if (currentUser?.id && autoSaveToFirestore) {
            const profileDocRef = doc(db, 'users', currentUser.id, 'private', 'profile');
            await secureSetDoc(profileDocRef, {
              userId: currentUser.id,
              fullName: fullName || currentUser.name || '',
              phoneNumber: phoneNumber || '',
              shippingAddress: shippingAddress || '',
              lineUserId: data.lineUserId,
              lastDeviceId: lastDeviceId || 'DEV-DEFAULT',
              updatedAt: new Date().toISOString()
            }, `users/${currentUser.id}/private/profile`);
          }
          return true;
        }
      }
      return false;
    } catch (e) {
      console.error('Error checking LINE status:', e);
      return false;
    } finally {
      setCheckingLineStatus(false);
    }
  };

  // Direct LINE Binding Handler
  const handleDirectBind = async (overrideId?: string) => {
    const targetId = (overrideId || manualLineId).trim();
    if (!targetId) {
      setLineModalMsg({ type: 'error', text: 'กรุณากรอก LINE User ID (ขึ้นต้นด้วย U)' });
      return;
    }
    try {
      setSavingLineBinding(true);
      setLineModalMsg(null);
      const res = await fetch('/api/line/bind-member', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          memberId,
          lineUserId: targetId
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setLineUserId(targetId);
        setManualLineId('');
        // Also update Firestore profile
        if (currentUser?.id) {
          const profileDocRef = doc(db, 'users', currentUser.id, 'private', 'profile');
          await secureSetDoc(profileDocRef, {
            userId: currentUser.id,
            fullName: fullName || currentUser.name || '',
            phoneNumber: phoneNumber || '',
            shippingAddress: shippingAddress || '',
            lineUserId: targetId,
            lastDeviceId: lastDeviceId || 'DEV-DEFAULT',
            updatedAt: new Date().toISOString()
          }, `users/${currentUser.id}/private/profile`);
        }
        setLineModalMsg({ type: 'success', text: 'ผูก LINE User ID สำเร็จแล้ว! ระบบพร้อมส่งการแจ้งเตือนคำสั่งซื้อ' });
        fetchDiagnostic();
      } else {
        setLineModalMsg({ type: 'error', text: data.error || 'ไม่สามารถผูกบัญชีได้' });
      }
    } catch (err: any) {
      setLineModalMsg({ type: 'error', text: err?.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ' });
    } finally {
      setSavingLineBinding(false);
    }
  };

  // Test Push Notification to LINE
  const handleTestPushNotification = async () => {
    if (!lineUserId) {
      setLineModalMsg({ type: 'error', text: 'กรุณาผูก LINE User ID ก่อนทดสอบส่งแจ้งเตือน' });
      return;
    }
    try {
      setSendingTestPush(true);
      setLineModalMsg(null);
      const res = await fetch('/api/line/test-push', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          memberId,
          lineUserId
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setLineModalMsg({ type: 'success', text: 'ส่งข้อความแจ้งเตือนทดสอบเข้า LINE เรียบร้อยแล้ว!' });
      } else {
        const errorText = data.error || data.message || 'ไม่สามารถส่งข้อความได้ (กรุณาตรวจสอบ Channel Access Token)';
        setLineModalMsg({
          type: 'error',
          text: errorText,
          details: data.details
        });
      }
      fetchDiagnostic();
    } catch (err: any) {
      setLineModalMsg({ type: 'error', text: err?.message || 'เกิดข้อผิดพลาดในการทดสอบ' });
      fetchDiagnostic();
    } finally {
      setSendingTestPush(false);
    }
  };

  const copyConnectCode = () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(`CONNECT_${memberId}`).catch(() => {});
      }
    } catch (e) {
      console.warn('Clipboard write error:', e);
    }
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const copyWebhookUrl = (url: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).catch(() => {});
      }
    } catch (e) {
      console.warn('Clipboard write error:', e);
    }
    setCopiedWebhook(true);
    setTimeout(() => setCopiedWebhook(false), 2000);
  };

  // Fetch Member Profile and Orders from Firestore/API
  const fetchMemberData = async () => {
    if (!currentUser) return;
    try {
      setLoading(true);
      setErrorMsg('');
      setSuccessMsg('');

      let profileName = fullName;
      let profilePhone = phoneNumber;

      // Fetch Profile document
      if (currentUser.id) {
        const profileDocRef = doc(db, 'users', currentUser.id, 'private', 'profile');
        const profileSnap = await secureGetDoc(profileDocRef, `users/${currentUser.id}/private/profile`);
        if (profileSnap && profileSnap.exists()) {
          const profileData = profileSnap.data() as any;
          profileName = profileData.fullName || profileName;
          profilePhone = profileData.phoneNumber || profilePhone;
          setFullName(profileName);
          setPhoneNumber(profilePhone);

          const fullAddr = profileData.shippingAddress || '';
          setShippingAddress(fullAddr);

          let a1 = profileData.address1 || '';
          let a2 = profileData.address2 || '';
          let a3 = profileData.address3 || '';
          if (!a1 && !a2 && !a3 && fullAddr) {
            const parsed = parseThaiAddress(fullAddr);
            a1 = parsed.addr1;
            a2 = parsed.addr2;
            a3 = parsed.addr3;
          }
          setAddress1(a1);
          setAddress2(a2);
          setAddress3(a3);

          setInitialFullName(profileName);
          setInitialPhoneNumber(profilePhone);
          setInitialShippingAddress(fullAddr);
          setInitialAddress1(a1);
          setInitialAddress2(a2);
          setInitialAddress3(a3);
          setLineUserId(profileData.lineUserId || '');
          
          // Detect or load device id
          let devId = profileData.lastDeviceId || '';
          if (!devId) {
            try {
              let localDev = localStorage.getItem('giff_device_id');
              if (!localDev) {
                localDev = 'DEV-' + Math.random().toString(36).substring(2, 8).toUpperCase() + '-' + Date.now().toString(36).toUpperCase();
                localStorage.setItem('giff_device_id', localDev);
              }
              devId = localDev;
            } catch (e) {
              devId = 'DEV-CLIENT-' + (currentUser?.id ? String(currentUser.id).substring(0, 8).toUpperCase() : 'UNKNOWN');
            }
          }
          setLastDeviceId(devId);
        } else {
          // If profile doc doesn't exist yet, generate device id
          try {
            let localDev = localStorage.getItem('giff_device_id');
            if (!localDev) {
              localDev = 'DEV-' + Math.random().toString(36).substring(2, 8).toUpperCase() + '-' + Date.now().toString(36).toUpperCase();
              localStorage.setItem('giff_device_id', localDev);
            }
            setLastDeviceId(localDev);
          } catch (e) {
            setLastDeviceId('DEV-CLIENT-DEFAULT');
          }
        }
      }

      // Fetch orders from server with strict Member ID isolation
      const targetMemberId = String(memberId || currentUser.customerId || '').trim();
      const targetIdUpper = targetMemberId.toUpperCase();
      const targetIdNoMem = targetIdUpper.replace(/^MEM/i, '');
      const userEmailLower = (currentUser.email || '').toLowerCase();

      const queryParam = targetMemberId 
        ? `?customerId=${encodeURIComponent(targetMemberId)}${userEmailLower ? `&email=${encodeURIComponent(userEmailLower)}` : ''}` 
        : (userEmailLower ? `?email=${encodeURIComponent(userEmailLower)}` : '');

      const res = await fetch(`/api/orders${queryParam}`);
      if (res.ok) {
        const data = await res.json();
        const userOrders = (data.orders || []).filter((o: any) => {
          const oCustId = (o.customerId || o.memberId || '').toString().trim().toUpperCase();
          const oCustNoMem = oCustId.replace(/^MEM/i, '');
          const oEmailLower = (o.customerEmail || o.email || '').toString().trim().toLowerCase();

          if (targetIdNoMem && oCustNoMem && oCustNoMem === targetIdNoMem) {
            return true;
          }
          if (userEmailLower && oEmailLower && oEmailLower === userEmailLower) {
            return true;
          }
          if (currentUser.id && o.userId && o.userId === currentUser.id) {
            return true;
          }
          return false;
        });
        setOrders(userOrders);
      }
    } catch (err: any) {
      console.error('Failed to load member data:', err);
      setErrorMsg('เกิดข้อผิดพลาดในการดึงข้อมูลจากระบบฐานข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchMemberData();
    }
  }, [currentUser, memberId]);

  // Reauthentication Helper
  const reauthenticateUser = async (passwordValue: string) => {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error('ไม่พบข้อมูลผู้ใช้ล็อกอิน');
    const credential = EmailAuthProvider.credential(user.email, passwordValue);
    await reauthenticateWithCredential(user, credential);
  };

  // Submit profile edits with strict mandatory fields (*) validation
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);
    if (!currentUser) return;

    // Strict validation of mandatory fields (*)
    if (!fullName.trim()) {
      setErrorMsg('กรุณากรอก "ชื่อ-นามสกุลจริง สมาชิก" (* จำเป็น)');
      return;
    }
    const cleanP = phoneNumber.replace(/\D/g, '');
    if (!cleanP || cleanP.length < 9 || cleanP.length > 10) {
      setErrorMsg('กรุณากรอก "เบอร์โทรศัพท์ติดต่อรับพัสดุ" ให้ถูกต้อง 9-10 หลัก (* จำเป็น)');
      return;
    }
    if (!address1.trim()) {
      setErrorMsg('กรุณากรอกที่อยู่จัดส่ง ช่องที่ 1: "บ้านเลขที่ ถนน ซอย / หมู่บ้านอาคาร" (* จำเป็น)');
      return;
    }
    if (!address2.trim()) {
      setErrorMsg('กรุณากรอกที่อยู่จัดส่ง ช่องที่ 2: "ตำบล/อำเภอ/จังหวัด" (* จำเป็น)');
      return;
    }
    if (!address3.trim() || address3.trim().length !== 5 || !/^\d{5}$/.test(address3.trim())) {
      setErrorMsg('กรุณากรอกที่อยู่จัดส่ง ช่องที่ 3: "รหัสไปรษณีย์ 5 หลัก" ให้ถูกต้อง (* จำเป็น)');
      return;
    }

    setSaving(true);
    setSuccessMsg('');
    setErrorMsg('');

    try {
      const combinedAddress = [address1.trim(), address2.trim(), address3.trim()].filter(Boolean).join(' ');
      const profileDocRef = doc(db, 'users', currentUser.id, 'private', 'profile');
      await secureSetDoc(profileDocRef, {
        userId: currentUser.id,
        fullName: fullName.trim(),
        phoneNumber: phoneNumber.trim(),
        shippingAddress: combinedAddress,
        address1: address1.trim(),
        address2: address2.trim(),
        address3: address3.trim(),
        lineUserId: lineUserId || '',
        lastDeviceId: lastDeviceId || '',
        updatedAt: new Date().toISOString()
      }, `users/${currentUser.id}/private/profile`);

      setShippingAddress(combinedAddress);
      setInitialFullName(fullName.trim());
      setInitialPhoneNumber(phoneNumber.trim());
      setInitialShippingAddress(combinedAddress);
      setInitialAddress1(address1.trim());
      setInitialAddress2(address2.trim());
      setInitialAddress3(address3.trim());
      setIsEditingProfile(false);
      setHasAttemptedSubmit(false);
      setSuccessMsg('บันทึกข้อมูลประวัติและที่อยู่จัดส่งพัสดุสะสมแต้ม PV สมาชิกสำเร็จเรียบร้อยแล้ว!');
    } catch (err: any) {
      setErrorMsg(err?.message || 'ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setSaving(false);
    }
  };

  // Submit Password Change
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg('');
    setErrorMsg('');

    if (newPassword !== confirmPassword) {
      setErrorMsg('รหัสผ่านใหม่ไม่ตรงกัน');
      return;
    }
    if (newPassword.length < 6) {
      setErrorMsg('รหัสผ่านใหม่ต้องมีความยาวอย่างน้อย 6 ตัวอักษร');
      return;
    }

    setSaving(true);
    try {
      // Reauthenticate first
      await reauthenticateUser(currentPassword);
      // Update Password
      const user = auth.currentUser;
      if (user) {
        await updatePassword(user, newPassword);
        setSuccessMsg('เปลี่ยนรหัสผ่านเข้าใช้งานใหม่สำเร็จแล้ว!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (err: any) {
      console.error('Password update error:', err);
      setErrorMsg(err?.message?.includes('auth/wrong-password') ? 'รหัสผ่านปัจจุบันไม่ถูกต้อง' : 'ไม่สามารถเปลี่ยนรหัสผ่านได้ กรุณาตรวจสอบรหัสผ่านเดิมให้ถูกต้อง');
    } finally {
      setSaving(false);
    }
  };

  // Submit PIN Change
  const handleChangePin = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg('');
    setErrorMsg('');

    if (newPin !== confirmPin) {
      setErrorMsg('พินใหม่ไม่ตรงกัน');
      return;
    }
    if (!/^\d{6}$/.test(newPin)) {
      setErrorMsg('รหัสพินความปลอดภัยต้องเป็นตัวเลข 6 หลักเท่านั้น');
      return;
    }

    setSaving(true);
    try {
      // Validate current pin
      if (currentUser.pin !== currentPin) {
        throw new Error('รหัสธุรกรรม PIN ปัจจุบันไม่ถูกต้อง');
      }

      // Write new PIN to Firestore user doc
      const userDocRef = doc(db, 'users', currentUser.id);
      await secureSetDoc(userDocRef, {
        ...currentUser,
        pin: newPin,
        updatedAt: new Date().toISOString()
      }, `users/${currentUser.id}`);

      setSuccessMsg('เปลี่ยนรหัส PIN ธุรกรรมความปลอดภัย 6 หลักของคุณสำเร็จ!');
      setCurrentPin('');
      setNewPin('');
      setConfirmPin('');
    } catch (err: any) {
      setErrorMsg(err?.message || 'เกิดข้อผิดพลาดในการเปลี่ยนรหัส PIN');
    } finally {
      setSaving(false);
    }
  };

  // Submit Email Change
  const handleChangeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg('');
    setErrorMsg('');

    if (!newEmail.includes('@')) {
      setErrorMsg('กรุณากรอกรูปแบบอีเมลให้ถูกต้อง');
      return;
    }

    setSaving(true);
    try {
      // Reauthenticate first
      await reauthenticateUser(currentPassForEmail);
      
      // Update Email in Firebase Auth
      const user = auth.currentUser;
      if (user) {
        await updateEmail(user, newEmail);

        // Update email inside user record in Firestore
        const userDocRef = doc(db, 'users', currentUser.id);
        await secureSetDoc(userDocRef, {
          ...currentUser,
          email: newEmail,
          updatedAt: new Date().toISOString()
        }, `users/${currentUser.id}`);

        setSuccessMsg('อัปเดตอีเมลเข้าใช้งานและรับข่าวสารพัสดุสำเร็จ!');
        setCurrentPassForEmail('');
        setNewEmail('');
      }
    } catch (err: any) {
      console.error('Email update error:', err);
      setErrorMsg(err?.message?.includes('auth/wrong-password') ? 'รหัสผ่านปัจจุบันไม่ถูกต้อง' : 'ไม่สามารถอัปเดตอีเมลได้ อีเมลนี้อาจถูกใช้งานไปแล้ว');
    } finally {
      setSaving(false);
    }
  };

  // Guard Condition: Unauthorized member tries to view another member's portal or direct access while logged out
  if (!currentUser || currentUser.customerId !== memberId) {
    return (
      <div className="max-w-xl mx-auto my-12 px-4 py-8 bg-white/95 rounded-2xl border border-pink-100 shadow-xl text-center">
        <div className="w-16 h-16 rounded-full bg-pink-50 text-[#DB2777] flex items-center justify-center mx-auto mb-4">
          <ShieldCheck className="w-8 h-8" />
        </div>
        <h2 className="text-base sm:text-lg font-black text-slate-800 mb-2">
          ระบบยืนยันสิทธิ์ความปลอดภัยส่วนบุคคล
        </h2>
        <p className="text-xs text-slate-500 leading-relaxed mb-6">
          คุณพยายามเข้าใช้งานแดชบอร์ดธุรกรรมส่วนตัวของสมาชิกรหัส <strong className="text-pink-600 font-bold">{memberId}</strong> <br />
          กรุณาเข้าสู่ระบบด้วยบัญชีที่ถูกต้องเพื่อปกป้องสิทธิ์ข้อมูลและการเปลี่ยนพินสั่งซื้อสินค้ากิฟฟารีน
        </p>
        <div className="flex gap-2 justify-center">
          <button
            onClick={onOpenAuth}
            className="px-5 py-2.5 bg-[#BE185D] hover:bg-pink-700 text-white font-extrabold text-xs sm:text-sm rounded-xl transition-all shadow-xs"
          >
            เข้าสู่ระบบสมาชิกรหัส {memberId}
          </button>
          <button
            onClick={() => window.location.href = '/'}
            className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-extrabold text-xs sm:text-sm rounded-xl transition-all"
          >
            กลับหน้าแรกช้อปปิ้ง
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl w-full mx-auto px-4 py-6 font-['Prompt',sans-serif]">
      {/* Upper Welcome Header */}
      <div className="pearl-white-card p-4 sm:p-5 rounded-3xl mb-6 flex flex-col sm:flex-row items-center justify-between gap-4 transition-all">
        <div className="flex items-center gap-3.5 w-full sm:w-auto">
          <div className="w-11 h-11 rounded-2xl bg-white/90 border border-pink-100 text-pink-700 flex items-center justify-center shrink-0 shadow-2xs p-1">
            <span className="text-[10px] font-black text-pink-800 tracking-tighter uppercase font-serif">Giffarine</span>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-lg font-extrabold text-slate-900 truncate sm:whitespace-normal">
              หน้าหลักสมาชิก
            </h1>
            <p className="text-xs text-slate-500 font-normal">
              จัดการโปรไฟล์ ประวัติการซื้อพัสดุ และรหัส PIN ธุรกรรมความปลอดภัย 6 หลักของคุณ
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5 w-full sm:w-auto justify-end flex-wrap">
          {/* Direct LINE Binding Button on Header */}
          <button
            type="button"
            onClick={() => {
              setIsLineModalOpen(true);
              setLineModalMsg(null);
              syncLineBindingStatus();
              fetchDiagnostic();
            }}
            className="flex-1 sm:flex-initial px-4 py-2 bg-[#06C755] hover:bg-[#05b34c] text-white font-bold text-xs rounded-full transition-all shadow-xs flex items-center justify-center gap-1.5 cursor-pointer border border-emerald-400/30 active:scale-95"
            title="ผูก LINE รับแจ้งเตือน & เช็คสถานะ Webhook"
          >
            <MessageSquare className="w-4 h-4" />
            <span>{lineUserId ? 'LINE: ผูกบัญชีแล้ว' : 'ผูก LINE แจ้งเตือน'}</span>
          </button>

          <button
            onClick={onLogout}
            className="px-4 py-2 bg-white/90 hover:bg-white text-slate-700 hover:text-rose-700 font-bold text-xs rounded-full transition-colors flex items-center justify-center gap-2 border border-pink-100 shadow-2xs cursor-pointer"
          >
            <LogOut className="w-4 h-4" />
            <span>ออกจากระบบ</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
        {/* Left Side Navigation Menu */}
        <div className="lg:col-span-1 pearl-white-card rounded-3xl p-3.5 space-y-2">
          {/* Top Home Button */}
          <button
            type="button"
            onClick={() => {
              window.history.pushState({}, '', '/');
              window.dispatchEvent(new Event('popstate'));
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="w-full px-4 py-3 rounded-2xl bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] hover:opacity-95 text-white font-bold text-sm shadow-xs transition-all flex items-center gap-2.5 cursor-pointer active:scale-98"
          >
            <Home className="w-4.5 h-4.5" />
            <span>หน้าแรก</span>
          </button>

          <div className="pt-2">
            <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider px-3 mb-2">
              <Compass className="w-3.5 h-3.5 text-slate-400" />
              <span>เมนูทางลัด</span>
            </div>
            
            <div className="space-y-1">
              {/* Menu item 1: ข้อมูลสมาชิกกิฟฟารีน */}
              <button
                onClick={() => { setActiveMenu('profile'); setSuccessMsg(''); setErrorMsg(''); }}
                className={`w-full px-3.5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center justify-between cursor-pointer ${
                  activeMenu === 'profile'
                    ? 'bg-slate-100 text-slate-900 font-extrabold shadow-2xs'
                    : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <User className={`w-4 h-4 ${activeMenu === 'profile' ? 'text-teal-700' : 'text-slate-500'}`} />
                  <span>ข้อมูลสมาชิกกิฟฟารีน</span>
                </div>
              </button>

              {/* Menu item LINE */}
              <button
                type="button"
                onClick={() => {
                  setIsLineModalOpen(true);
                  setLineModalMsg(null);
                  syncLineBindingStatus();
                  fetchDiagnostic();
                }}
                className="w-full px-3.5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center justify-between text-slate-700 hover:bg-slate-50 cursor-pointer active:scale-98"
              >
                <div className="flex items-center gap-2.5 min-w-0 pr-1">
                  <MessageSquare className="w-4 h-4 text-slate-500 shrink-0" />
                  <span className="truncate">แจ้งเตือน LINE & ตรวจ...</span>
                </div>
                {lineUserId ? (
                  <span className="text-[10px] px-2 py-0.5 bg-[#06C755] text-white font-bold rounded-full shrink-0">
                    ผูกแล้ว
                  </span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-900 font-bold rounded-full border border-amber-300 shrink-0">
                    ยังไม่ผูก
                  </span>
                )}
              </button>

              {/* Menu item 2: ประวัติคำสั่งซื้อของฉัน */}
              <button
                onClick={() => { setActiveMenu('orders'); setSuccessMsg(''); setErrorMsg(''); }}
                className={`w-full px-3.5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center justify-between cursor-pointer ${
                  activeMenu === 'orders'
                    ? 'bg-slate-100 text-slate-900 font-extrabold shadow-2xs'
                    : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <History className={`w-4 h-4 ${activeMenu === 'orders' ? 'text-teal-700' : 'text-slate-500'}`} />
                  <span>ประวัติคำสั่งซื้อของฉัน</span>
                </div>
              </button>

              {/* Menu item 3: เปลี่ยนรหัสผ่าน */}
              <button
                onClick={() => { setActiveMenu('password'); setSuccessMsg(''); setErrorMsg(''); }}
                className={`w-full px-3.5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center justify-between cursor-pointer ${
                  activeMenu === 'password'
                    ? 'bg-slate-100 text-slate-900 font-extrabold shadow-2xs'
                    : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <Lock className={`w-4 h-4 ${activeMenu === 'password' ? 'text-teal-700' : 'text-slate-500'}`} />
                  <span>เปลี่ยนรหัสผ่าน</span>
                </div>
              </button>

              {/* Menu item 4: เปลี่ยนรหัสพิน (PIN) */}
              <button
                onClick={() => { setActiveMenu('pin'); setSuccessMsg(''); setErrorMsg(''); }}
                className={`w-full px-3.5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center justify-between cursor-pointer ${
                  activeMenu === 'pin'
                    ? 'bg-slate-100 text-slate-900 font-extrabold shadow-2xs'
                    : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <ShieldCheck className={`w-4 h-4 ${activeMenu === 'pin' ? 'text-teal-700' : 'text-slate-500'}`} />
                  <span>เปลี่ยนรหัสพิน (PIN)</span>
                </div>
              </button>

              {/* Menu item 5: เปลี่ยนอีเมลเข้าใช้งาน */}
              <button
                onClick={() => { setActiveMenu('email'); setSuccessMsg(''); setErrorMsg(''); }}
                className={`w-full px-3.5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center justify-between cursor-pointer ${
                  activeMenu === 'email'
                    ? 'bg-slate-100 text-slate-900 font-extrabold shadow-2xs'
                    : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <Mail className={`w-4 h-4 ${activeMenu === 'email' ? 'text-teal-700' : 'text-slate-500'}`} />
                  <span>เปลี่ยนอีเมลเข้าใช้งาน</span>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Right Side Working Workspace Panel */}
        <div className="lg:col-span-3 space-y-5">
          {successMsg && (
            <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center gap-2 text-emerald-700 text-xs font-semibold shadow-2xs">
              <CheckCircle2 className="w-4.5 h-4.5 text-emerald-600 shrink-0" />
              <span>{successMsg}</span>
            </div>
          )}

          {errorMsg && (
            <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-2xl flex items-center gap-2 text-rose-700 text-xs font-semibold shadow-2xs">
              <AlertCircle className="w-4.5 h-4.5 text-rose-600 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {loading ? (
            <div className="bg-white/95 rounded-3xl border border-slate-200/80 p-12 flex flex-col items-center justify-center gap-2 text-teal-600 min-h-[420px]">
              <RotateCw className="w-8 h-8 animate-spin" />
              <span className="text-xs text-slate-400 font-bold">กำลังประสานงานคลาวด์ฐานข้อมูล...</span>
            </div>
          ) : activeMenu === 'profile' ? (
            /* Tab: ข้อมูลสมาชิก (ตรงตามตัวอย่างภาพที่ระบุ) */
            <div className="space-y-5">
              {/* 1 บับเบิ้ลรวม: บัตรประจำตัวสมาชิกดิจิทัลและฟอร์มข้อมูลที่อยู่จัดส่งพัสดุสะสมแต้ม PV */}
              <div className="pearl-white-card rounded-3xl border border-pink-200/80 shadow-sm overflow-hidden flex flex-col">
                {/* ส่วนบนของบับเบิ้ล: แถบสถานะบัตรสมาชิก GIFFARINE AUTHORIZED MEMBER */}
                <div className="bg-gradient-to-r from-[#062c2b] via-[#093d3b] to-[#041d1d] text-white p-5 sm:p-6 relative overflow-hidden border-b border-teal-500/20">
                  <div className="absolute -right-8 -bottom-8 w-44 h-44 bg-teal-400/10 rounded-full blur-2xl pointer-events-none" />
                  
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-teal-300/90 flex items-center gap-1.5">
                          <ShieldCheck className="w-4 h-4 text-teal-400" />
                          <span>GIFFARINE AUTHORIZED MEMBER</span>
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-3">
                        <div>
                          <span className="text-[10px] sm:text-[11px] text-slate-300/80 font-medium block">รหัสสมาชิก Giffarine ID</span>
                          <span className="text-2xl sm:text-3xl font-black font-mono tracking-wider text-white">
                            {memberId}
                          </span>
                        </div>
                        
                        <div className="sm:ml-1">
                          {lineUserId ? (
                            <button
                              type="button"
                              onClick={() => {
                                setIsLineModalOpen(true);
                                setLineModalMsg(null);
                                fetchDiagnostic();
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#06C755] hover:bg-[#05b34c] text-white rounded-full text-xs font-bold shadow-xs transition-all cursor-pointer border border-white/20 active:scale-95"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                              <span>ผูก LINE แล้ว</span>
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setIsLineModalOpen(true);
                                setLineModalMsg(null);
                                syncLineBindingStatus();
                                fetchDiagnostic();
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#06C755] hover:bg-[#05b34c] text-white rounded-full text-xs font-bold shadow-xs transition-all cursor-pointer border border-white/20 active:scale-95"
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                              <span>+ ผูก LINE</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:flex md:items-center gap-4 md:gap-6 text-xs text-slate-300 pt-3 md:pt-0 border-t md:border-t-0 border-teal-800/60">
                      <div>
                        <span className="opacity-70 block text-[10px]">อีเมลสมาชิก</span>
                        <span className="font-bold truncate block text-white text-xs max-w-[220px]">
                          {currentUser?.email || '-'}
                        </span>
                      </div>
                      <div>
                        <span className="opacity-70 block text-[10px]">เข้าร่วมระบบ</span>
                        <span className="font-bold block text-white text-xs">
                          {currentUser?.createdAt ? new Date(currentUser.createdAt).toLocaleDateString('th-TH') : '13/9/2569'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ส่วนล่างของบับเบิ้ล: ฟอร์มข้อมูลและที่อยู่จัดส่งพัสดุสะสมแต้ม PV */}
                <div className="p-5 sm:p-6 bg-white/95 flex flex-col justify-between">
                  <form onSubmit={handleUpdateProfile} className="space-y-4">
                    {/* Header with status and Edit button */}
                    <div className="flex items-center justify-between pb-3 border-b border-pink-100/70">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-pink-500 to-[#E60067] text-white flex items-center justify-center shadow-xs">
                          <MapPin className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-black text-[#9D174D]">
                            ข้อมูลประวัติและที่อยู่จัดส่งพัสดุสะสมแต้ม PV
                          </h3>
                          <p className="text-[11px] text-slate-500">
                            ข้อมูลสำหรับจัดส่งสินค้าและบันทึกคะแนน PV สมาชิก
                          </p>
                        </div>
                      </div>

                      {!isEditingProfile && (
                        <button
                          type="button"
                          onClick={() => setIsEditingProfile(true)}
                          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-pink-50 hover:bg-pink-100 text-[#E60067] text-xs font-bold rounded-xl border border-pink-200 shadow-2xs transition-all cursor-pointer active:scale-95"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                          <span>แก้ไขข้อมูล</span>
                        </button>
                      )}
                    </div>

                    {/* Member Profile Inputs matching Checkout Form Pattern */}
                    <div className="space-y-3.5">
                      {/* Grid 2 Columns: Full Name & Phone Number */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        {/* 1. Full Name */}
                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">
                            ชื่อ-นามสกุลจริง สมาชิก <span className="text-rose-500 font-bold ml-0.5">*</span>
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              required
                              readOnly={!isEditingProfile}
                              disabled={!isEditingProfile}
                              value={fullName}
                              onChange={(e) => setFullName(e.target.value)}
                              placeholder="กรอกชื่อ-นามสกุลจริง"
                              className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm transition-all ${
                                !isEditingProfile
                                  ? 'border-slate-200 bg-slate-50/80 text-slate-800 cursor-not-allowed select-none'
                                  : hasAttemptedSubmit && !isFullNameValid
                                  ? 'border-rose-400 bg-rose-50/10 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 text-slate-900'
                                  : 'border-pink-200/80 focus:border-pink-500 focus:ring-1 focus:ring-pink-500 bg-white text-slate-900 shadow-2xs'
                              }`}
                            />
                            <User className="absolute left-3.5 top-3 w-4 h-4 text-pink-500 pointer-events-none" />
                          </div>
                          {hasAttemptedSubmit && !isFullNameValid && isEditingProfile && (
                            <span className="text-[10px] font-semibold text-rose-500 mt-1 block">
                              * กรุณากรอกชื่อ-นามสกุลจริง สมาชิก (จำเป็น)
                            </span>
                          )}
                        </div>

                        {/* 2. Phone Number */}
                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">
                            เบอร์โทรศัพท์ติดต่อรับพัสดุ <span className="text-rose-500 font-bold ml-0.5">*</span>
                          </label>
                          <div className="relative">
                            <input
                              type="tel"
                              required
                              readOnly={!isEditingProfile}
                              disabled={!isEditingProfile}
                              value={phoneNumber}
                              onChange={(e) => {
                                const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                                setPhoneNumber(digits);
                              }}
                              placeholder="เช่น 0812345678 (9-10 หลัก)"
                              className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm transition-all ${
                                !isEditingProfile
                                  ? 'border-slate-200 bg-slate-50/80 text-slate-800 cursor-not-allowed select-none'
                                  : hasAttemptedSubmit && !isPhoneValid
                                  ? 'border-rose-400 bg-rose-50/10 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 text-slate-900'
                                  : 'border-pink-200/80 focus:border-pink-500 focus:ring-1 focus:ring-pink-500 bg-white text-slate-900 shadow-2xs'
                              }`}
                            />
                            <Phone className="absolute left-3.5 top-3 w-4 h-4 text-pink-500 pointer-events-none" />
                          </div>
                          {hasAttemptedSubmit && !isPhoneValid && isEditingProfile && (
                            <span className="text-[10px] font-semibold text-rose-500 mt-1 block">
                              * กรุณากรอกเบอร์โทรศัพท์ติดต่อ 9-10 หลัก (จำเป็น)
                            </span>
                          )}
                        </div>
                      </div>

                      {/* 3. Shipping Address 3 structured rows */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          ที่อยู่จัดส่งสินค้าพัสดุสะสม <span className="text-rose-500 font-bold ml-0.5">*</span>
                        </label>
                        <div className="space-y-2 relative">
                          {/* ช่องที่ 1 */}
                          <div>
                            <div className="relative">
                              <input
                                type="text"
                                required
                                readOnly={!isEditingProfile}
                                disabled={!isEditingProfile}
                                value={address1}
                                onChange={(e) => setAddress1(e.target.value)}
                                placeholder="ช่องที่ 1: บ้านเลขที่ ถนน ซอย / หมู่บ้านอาคาร"
                                className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm transition-all ${
                                  !isEditingProfile
                                    ? 'border-slate-200 bg-slate-50/80 text-slate-800 cursor-not-allowed select-none'
                                    : hasAttemptedSubmit && !isAddress1Valid
                                    ? 'border-rose-400 bg-rose-50/10 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 text-slate-900'
                                    : 'border-pink-200/80 focus:border-pink-500 focus:ring-1 focus:ring-pink-500 bg-white text-slate-900 shadow-2xs'
                                }`}
                              />
                              <MapPin className="absolute left-3.5 top-3 w-4 h-4 text-pink-500 pointer-events-none" />
                            </div>
                            {hasAttemptedSubmit && !isAddress1Valid && isEditingProfile && (
                              <span className="text-[10px] font-semibold text-rose-500 mt-0.5 block">
                                * กรุณากรอกบ้านเลขที่ ถนน ซอย (จำเป็น)
                              </span>
                            )}
                          </div>

                          {/* ช่องที่ 2: Sub-district / District / Province with Autocomplete */}
                          <div className="relative">
                            <div className="relative">
                              <input
                                type="text"
                                required
                                readOnly={!isEditingProfile}
                                disabled={!isEditingProfile}
                                value={address2}
                                onChange={(e) => handlePart2Change(e.target.value)}
                                onFocus={() => {
                                  if (isEditingProfile && addressSuggestions.length > 0) setShowSuggestions(true);
                                }}
                                placeholder="ช่องที่ 2: ตำบล/อำเภอ/จังหวัด"
                                className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm transition-all ${
                                  !isEditingProfile
                                    ? 'border-slate-200 bg-slate-50/80 text-slate-800 cursor-not-allowed select-none'
                                    : hasAttemptedSubmit && !isAddress2Valid
                                    ? 'border-rose-400 bg-rose-50/10 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 text-slate-900'
                                    : 'border-pink-200/80 focus:border-pink-500 focus:ring-1 focus:ring-pink-500 bg-white text-slate-900 shadow-2xs'
                                }`}
                              />
                              <MapPin className="absolute left-3.5 top-3 w-4 h-4 text-pink-500 pointer-events-none" />
                            </div>
                            {hasAttemptedSubmit && !isAddress2Valid && isEditingProfile && (
                              <span className="text-[10px] font-semibold text-rose-500 mt-0.5 block">
                                * กรุณากรอกตำบล อำเภอ และจังหวัด (จำเป็น)
                              </span>
                            )}
                          </div>

                          {/* ช่องที่ 3: 5-digit Postal code */}
                          <div className="relative">
                            <div className="relative">
                              <input
                                type="text"
                                required
                                maxLength={5}
                                readOnly={!isEditingProfile}
                                disabled={!isEditingProfile}
                                value={address3}
                                onChange={(e) => handlePart3Change(e.target.value)}
                                onFocus={() => {
                                  if (isEditingProfile && addressSuggestions.length > 0) setShowSuggestions(true);
                                }}
                                placeholder="ช่องที่ 3: รหัสไปรษณีย์ 5 หลัก"
                                className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm font-mono transition-all ${
                                  !isEditingProfile
                                    ? 'border-slate-200 bg-slate-50/80 text-slate-800 cursor-not-allowed select-none'
                                    : hasAttemptedSubmit && !isAddress3Valid
                                    ? 'border-rose-400 bg-rose-50/10 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 text-slate-900'
                                    : 'border-pink-200/80 focus:border-pink-500 focus:ring-1 focus:ring-pink-500 bg-white text-slate-900 shadow-2xs'
                                }`}
                              />
                              <MapPin className="absolute left-3.5 top-3 w-4 h-4 text-pink-500 pointer-events-none" />
                            </div>
                            {hasAttemptedSubmit && !isAddress3Valid && isEditingProfile && (
                              <span className="text-[10px] font-semibold text-rose-500 mt-0.5 block">
                                * กรุณากรอกรหัสไปรษณีย์ 5 หลักให้ถูกต้อง (จำเป็น)
                              </span>
                            )}

                            {/* Suggestions dropdown */}
                            {showSuggestions && addressSuggestions.length > 0 && isEditingProfile && (
                              <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-pink-200 rounded-xl shadow-xl max-h-48 overflow-y-auto z-30 divide-y divide-pink-50">
                                {addressSuggestions.map((item, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={() => handleSelectAddress(item)}
                                    className="w-full text-left px-3.5 py-2 hover:bg-pink-50 transition-colors flex items-center justify-between text-xs text-slate-700 cursor-pointer"
                                  >
                                    <span>
                                      <strong className="text-pink-600">ต.{item.s}</strong> อ.{item.d} จ.{item.p}
                                    </span>
                                    <span className="font-mono font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded text-[11px]">
                                      {item.z}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Security and System identifiers */}
                      <div className="pt-2 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 mb-1">
                            Line user id (ระบบเชื่อมโยง)
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              readOnly
                              disabled
                              value={maskAfterFiveChars(lineUserId)}
                              placeholder="-"
                              className="w-full pl-7 pr-7 py-1.5 rounded-lg border border-slate-200 text-[11px] font-mono bg-slate-50/80 text-slate-500 cursor-not-allowed select-none"
                            />
                            <MessageSquare className="absolute left-2.5 top-2 w-3 h-3 text-slate-400" />
                            <Lock className="absolute right-2.5 top-2 w-3 h-3 text-slate-400" />
                          </div>
                        </div>

                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 mb-1">
                            หมายเลขอุปกรณ์ คำสั่งซื้อใช้งาน
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              readOnly
                              disabled
                              value={maskAfterFiveChars(lastDeviceId)}
                              placeholder="-"
                              className="w-full pl-7 pr-7 py-1.5 rounded-lg border border-slate-200 text-[11px] font-mono bg-slate-50/80 text-slate-500 cursor-not-allowed select-none"
                            />
                            <Smartphone className="absolute left-2.5 top-2 w-3 h-3 text-slate-400" />
                            <Lock className="absolute right-2.5 top-2 w-3 h-3 text-slate-400" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Footer buttons / Validation notification */}
                    {isEditingProfile ? (
                      <div className="space-y-2 pt-2 border-t border-pink-100">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setFullName(initialFullName);
                              setPhoneNumber(initialPhoneNumber);
                              setShippingAddress(initialShippingAddress);
                              setAddress1(initialAddress1);
                              setAddress2(initialAddress2);
                              setAddress3(initialAddress3);
                              setIsEditingProfile(false);
                              setHasAttemptedSubmit(false);
                              setShowSuggestions(false);
                              setErrorMsg('');
                            }}
                            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-bold rounded-xl transition-colors cursor-pointer"
                          >
                            ยกเลิก
                          </button>
                          <button
                            type="submit"
                            disabled={saving || !isFormValid}
                            className="px-5 py-2 bg-gradient-to-r from-pink-600 to-[#E60067] hover:from-pink-700 hover:to-[#c70059] text-white text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-sm transition-all active:scale-98 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                            title={!isFormValid ? 'กรุณากรอกข้อมูลที่มี * ให้ครบทุกช่องก่อนบันทึก' : 'บันทึกข้อมูล'}
                          >
                            <Save className="w-4 h-4" />
                            <span>{saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}</span>
                          </button>
                        </div>

                        {!isFormValid && (
                          <div className="p-2.5 rounded-xl bg-amber-50 border border-amber-200/80 text-[11px] text-amber-800 flex items-center gap-1.5">
                            <AlertCircle className="w-4 h-4 shrink-0 text-amber-600" />
                            <span>
                              <strong>เงื่อนไขสำคัญ:</strong> ข้อมูลที่มีเครื่องหมาย <span className="text-rose-600 font-bold">(*)</span> กำกับ ต้องบังคับกรอกให้ครบทุกช่อง หากกรอกไม่ครบจะไม่สามารถบันทึกข้อมูลได้
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-[11px] text-slate-500 pt-2 border-t border-slate-100 flex items-center justify-between flex-wrap gap-1">
                        <span>* หากต้องการปรับแก้ไขข้อมูลติดต่อ กรุณากดปุ่ม <strong className="text-[#9D174D]">"แก้ไขข้อมูล"</strong> ด้านบน</span>
                        <span className="text-[10px] text-slate-400">ระบบคลาวด์เข้ารหัสความปลอดภัย SSL/TLS</span>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          ) : activeMenu === 'orders' ? (
            /* Tab: ประวัติคำสั่งซื้อ (ดีไซน์สไตล์คลีนแบบเดียวกับ Seller Admin Dashboard) */
            <div className="space-y-4">
              <div className="pearl-white-card rounded-2xl overflow-hidden">
                {/* Header bar matching seller dashboard */}
                <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3 bg-slate-50/50">
                  <div>
                    <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                      <History className="w-4 h-4 text-teal-600" />
                      <span>ประวัติคำสั่งซื้อทั้งหมด ({orders.filter(o => orderStatusFilter === 'all' || o.orderStatus === orderStatusFilter).length} รายการ)</span>
                    </h3>
                    <p className="text-xs text-slate-500">ตรวจสอบสถานะการชำระเงิน หมายเลขพัสดุจัดส่ง และเปิดดูสลิปใบเสร็จรับเงิน</p>
                  </div>

                  {/* Status filter dropdown */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 font-medium">สถานะ:</span>
                    <select
                      value={orderStatusFilter}
                      onChange={(e) => setOrderStatusFilter(e.target.value)}
                      className="text-xs bg-white border border-slate-200 rounded-lg px-3 py-1.5 font-bold text-slate-700 focus:outline-none cursor-pointer shadow-2xs"
                    >
                      <option value="all">ทั้งหมด ({orders.length})</option>
                      <option value="Wait_Verification">รอตรวจสอบสลิป</option>
                      <option value="Pending_Shipment">รอการจัดส่ง</option>
                      <option value="Processing">กำลังจัดเตรียม</option>
                      <option value="Shipped">จัดส่งแล้ว</option>
                      <option value="Completed">เสร็จสิ้นสมบูรณ์</option>
                    </select>
                  </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-slate-600 border-b border-slate-200 font-black text-[11px]">
                      <tr>
                        <th className="p-3.5 whitespace-nowrap">ID คำสั่งซื้อ</th>
                        <th className="p-3.5 whitespace-nowrap">วันเวลา</th>
                        <th className="p-3.5 min-w-[200px]">รายการสินค้า</th>
                        <th className="p-3.5 whitespace-nowrap">ยอดรวมสุทธิ</th>
                        <th className="p-3.5 whitespace-nowrap">คะแนน PV</th>
                        <th className="p-3.5 whitespace-nowrap">วิธีชำระ</th>
                        <th className="p-3.5 whitespace-nowrap">สถานะออเดอร์</th>
                        <th className="p-3.5 min-w-[170px] whitespace-nowrap">หมายเลขติดตามพัสดุ</th>
                        <th className="p-3.5 text-right whitespace-nowrap">การจัดการ</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {orders
                        .filter((o) => orderStatusFilter === 'all' || o.orderStatus === orderStatusFilter)
                        .map((o) => (
                        <tr key={o.orderId} className="hover:bg-slate-50/60 transition-colors">
                          {/* ID คำสั่งซื้อ */}
                          <td className="p-3.5 font-mono font-bold text-teal-700 whitespace-nowrap">
                            {o.orderId}
                          </td>

                          {/* วันเวลา */}
                          <td className="p-3.5 text-slate-500 whitespace-nowrap font-mono">
                            {formatThaiDateTime(o.timestamp)}
                          </td>

                          {/* รายการสินค้า */}
                          <td className="p-3.5 text-slate-800">
                            <div className="space-y-1">
                              {o.items && o.items.length > 0 ? (
                                o.items.map((item, idx) => (
                                  <div key={idx} className="flex justify-between items-start gap-2 text-[11.5px] leading-relaxed">
                                    <span className="text-slate-700 font-medium line-clamp-1">• {item.productName}</span>
                                    <span className="text-slate-900 font-bold shrink-0">x{item.quantity}</span>
                                  </div>
                                ))
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </div>
                          </td>

                          {/* ยอดรวมสุทธิ */}
                          <td className="p-3.5 font-black text-slate-900 whitespace-nowrap">
                            ฿{o.totalAmount?.toLocaleString()}
                          </td>

                          {/* คะแนน PV */}
                          <td className="p-3.5 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold bg-amber-50 text-amber-800 border border-amber-200/80">
                              <span>🪙</span>
                              <span>{o.totalPv || 0} PV</span>
                            </span>
                          </td>

                          {/* วิธีชำระ */}
                          <td className="p-3.5 whitespace-nowrap">
                            <span className={`px-2.5 py-1 rounded-md text-[10.5px] font-bold ${
                              o.paymentMethod === 'BANK_TRANSFER'
                                ? 'bg-blue-50 text-blue-700 border border-blue-100'
                                : 'bg-amber-50 text-amber-700 border border-amber-100'
                            }`}>
                              {o.paymentMethod === 'BANK_TRANSFER' ? 'BANK_TRANSFER' : o.paymentMethod || 'COD'}
                            </span>
                          </td>

                          {/* สถานะออเดอร์ */}
                          <td className="p-3.5 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold ${
                              o.orderStatus === 'Completed'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : o.orderStatus === 'Shipped'
                                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                  : o.orderStatus === 'Processing'
                                    ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                    : o.orderStatus === 'Pending_Shipment'
                                      ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                      : 'bg-slate-100 text-slate-700 border border-slate-200'
                            }`}>
                              {o.orderStatus || 'Wait_Verification'}
                            </span>
                          </td>

                          {/* หมายเลขติดตามพัสดุ */}
                          <td className="p-3.5 whitespace-nowrap">
                            {o.trackingNumber ? (
                              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-teal-50 border border-teal-200 text-teal-800 font-mono font-bold text-xs">
                                <Truck className="w-3.5 h-3.5 text-teal-600 shrink-0" />
                                <span>{o.trackingNumber}</span>
                              </div>
                            ) : (
                              <span className="text-slate-400 font-medium text-xs">-</span>
                            )}
                          </td>

                          {/* การจัดการ */}
                          <td className="p-3.5 text-right whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => setSelectedOrderForSlip(o)}
                              className="px-3 py-1.5 bg-teal-50 hover:bg-teal-100 text-teal-700 font-bold rounded-lg text-xs transition-colors shadow-2xs inline-flex items-center gap-1 cursor-pointer active:scale-95"
                            >
                              เปิดสลิปใบเสร็จ
                            </button>
                          </td>
                        </tr>
                      ))}

                      {orders.filter((o) => orderStatusFilter === 'all' || o.orderStatus === orderStatusFilter).length === 0 && (
                        <tr>
                          <td colSpan={9} className="p-12 text-center text-slate-400 font-medium">
                            {orders.length === 0 ? 'ยังไม่มีรายการคำสั่งซื้อสะสมแต้ม PV ในระบบ' : 'ไม่พบรายการคำสั่งซื้อตามตัวกรองที่เลือก'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : activeMenu === 'password' ? (
            /* Tab: เปลี่ยนรหัสผ่าน */
            <form onSubmit={handleChangePassword} className="pearl-white-card rounded-3xl p-5 sm:p-6 space-y-4">
              <h3 className="text-sm font-bold text-slate-900 border-b border-slate-100 pb-2">
                เปลี่ยนรหัสผ่านเข้าสู่ระบบ (Change Password)
              </h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                กรุณากรอกรหัสผ่านเดิมและรหัสใหม่ที่คุณต้องการ เพื่อทำการเปลี่ยนการเข้าถึงแอปอย่างปลอดภัย
              </p>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  รหัสผ่านปัจจุบัน <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    required
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="รหัสผ่านเข้าใช้ระบบเดิม"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 focus:outline-none text-sm bg-white text-slate-800"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3.5 top-3.5 text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  รหัสผ่านใหม่ (ต้องมีอย่างน้อย 6 หลัก) <span className="text-rose-500">*</span>
                </label>
                <input
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="รหัสผ่านเข้าใช้ระบบใหม่"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 focus:outline-none text-sm bg-white text-slate-800"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  ยืนยันรหัสผ่านใหม่ <span className="text-rose-500">*</span>
                </label>
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="กรอกรหัสผ่านใหม่อีกครั้ง"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 focus:outline-none text-sm bg-white text-slate-800"
                />
              </div>

              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-xs sm:text-sm font-bold rounded-xl flex items-center gap-2 shadow-xs transition-colors ml-auto active:scale-98 disabled:opacity-50 cursor-pointer"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'กำลังเปลี่ยนรหัสผ่าน...' : 'บันทึกเปลี่ยนรหัสผ่าน'}</span>
              </button>
            </form>
          ) : activeMenu === 'pin' ? (
            /* Tab: เปลี่ยนรหัสพิน */
            <form onSubmit={handleChangePin} className="pearl-white-card rounded-3xl p-5 sm:p-6 space-y-4">
              <h3 className="text-sm font-bold text-slate-900 border-b border-slate-100 pb-2">
                เปลี่ยนรหัสพินทำรายการธุรกรรม (Change 6-digit PIN)
              </h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                รหัสพินมีความจำเป็นสำหรับความปลอดภัยขั้นสูงสุดในการสร้างใบสั่งสินค้าของสมาชิกร้านค้ากิฟฟารีน
              </p>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  รหัส PIN 6 หลัก ปัจจุบัน <span className="text-rose-500">*</span>
                </label>
                <input
                  type="password"
                  pattern="\d*"
                  maxLength={6}
                  required
                  value={currentPin}
                  onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="รหัส PIN 6 หลักปัจจุบัน"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 focus:outline-none text-sm bg-white font-mono text-center tracking-widest text-slate-800"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  รหัส PIN 6 หลัก ใหม่ <span className="text-rose-500">*</span>
                </label>
                <input
                  type="password"
                  pattern="\d*"
                  maxLength={6}
                  required
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="รหัส PIN 6 หลักใหม่"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 focus:outline-none text-sm bg-white font-mono text-center tracking-widest text-slate-800"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  ยืนยันรหัส PIN 6 หลัก ใหม่ <span className="text-rose-500">*</span>
                </label>
                <input
                  type="password"
                  pattern="\d*"
                  maxLength={6}
                  required
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="กรอกรหัส PIN ใหม่อีกครั้งเพื่อยืนยัน"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 focus:outline-none text-sm bg-white font-mono text-center tracking-widest text-slate-800"
                />
              </div>

              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-xs sm:text-sm font-bold rounded-xl flex items-center gap-2 shadow-xs transition-colors ml-auto active:scale-98 disabled:opacity-50 cursor-pointer"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'กำลังดำเนินการ...' : 'บันทึกเปลี่ยนรหัส PIN'}</span>
              </button>
            </form>
          ) : (
            /* Tab: เปลี่ยนอีเมล */
            <form onSubmit={handleChangeEmail} className="pearl-white-card rounded-3xl p-5 sm:p-6 space-y-4">
              <h3 className="text-sm font-bold text-slate-900 border-b border-slate-100 pb-2">
                เปลี่ยนอีเมลสำหรับลงชื่อเข้าใช้งาน (Change Email)
              </h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                กรุณากรอกรหัสผ่านยืนยันสิทธิ์ของบัญชี เพื่อทำการเปลี่ยนอีเมลเข้าใช้งานใหม่
              </p>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  รหัสผ่านปัจจุบัน เพื่อยืนยันสิทธิ์ <span className="text-rose-500">*</span>
                </label>
                <input
                  type="password"
                  required
                  value={currentPassForEmail}
                  onChange={(e) => setCurrentPassForEmail(e.target.value)}
                  placeholder="รหัสผ่านปัจจุบันสำหรับการยืนยัน"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 focus:outline-none text-sm bg-white text-slate-800"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  อีเมลเข้าใช้งานใหม่ <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="เช่น example@newdomain.com"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 focus:outline-none text-sm bg-white text-slate-800"
                />
              </div>

              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-xs sm:text-sm font-bold rounded-xl flex items-center gap-2 shadow-xs transition-colors ml-auto active:scale-98 disabled:opacity-50 cursor-pointer"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'กำลังเปลี่ยนอีเมล...' : 'บันทึกเปลี่ยนอีเมล'}</span>
              </button>
            </form>
          )}
        </div>
      </div>

      {/* LINE Notification & Friend Binding Modal */}
      {isLineModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl border border-slate-200 animate-scale-up flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#06C755] to-[#05b34c] p-5 text-white flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-xs shadow-inner">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-black leading-tight">
                    ระบบแจ้งเตือนผ่าน LINE
                  </h3>
                  <p className="text-xs text-green-100 font-medium mt-0.5">
                    รับใบเสร็จคำสั่งซื้อและสถานะพัสดุเข้า LINE ทันที
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsLineModalOpen(false)}
                className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 sm:p-6 space-y-4 overflow-y-auto flex-1">
              {/* Alert Feedback Message */}
              {lineModalMsg && (
                <div className={`p-3.5 rounded-2xl text-xs font-semibold flex items-start gap-2.5 ${
                  lineModalMsg.type === 'success'
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                    : 'bg-rose-50 text-rose-800 border border-rose-200'
                }`}>
                  {lineModalMsg.type === 'success' ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className="font-bold">{lineModalMsg.text}</p>
                  </div>
                </div>
              )}

              {/* Status Section */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/80 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">
                    สถานะการเชื่อมต่อ (รหัสสมาชิก: {memberId})
                  </span>
                  {lineUserId ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-black rounded-full">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      เชื่อมต่อแล้ว
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded-full">
                      <span className="w-2 h-2 rounded-full bg-amber-500" />
                      ยังไม่ได้เชื่อมต่อ LINE
                    </span>
                  )}
                </div>

                {lineUserId ? (
                  <div className="space-y-3 pt-1">
                    <div className="text-xs bg-emerald-50 text-emerald-900 border border-emerald-200 px-3.5 py-2.5 rounded-xl flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span className="font-medium">บัญชี LINE ของคุณผูกกับรหัสสมาชิกเรียบร้อยแล้ว พร้อมรับใบเสร็จและแจ้งเตือนพัสดุอัตโนมัติ</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        disabled={sendingTestPush}
                        onClick={handleTestPushNotification}
                        className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-xs flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer active:scale-98"
                      >
                        <Bell className="w-3.5 h-3.5" />
                        <span>{sendingTestPush ? 'กำลังส่งแจ้งเตือน...' : '🔔 ทดสอบส่งข้อความแจ้งเตือนเข้า LINE'}</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-600 leading-relaxed">
                    เมื่อคุณเชื่อมต่อ LINE กับรหัสสมาชิก <strong>{memberId}</strong> เรียบร้อยแล้ว ระบบจะส่งใบเสร็จคำสั่งซื้อและยอดสะสม PV เข้าสู่ LINE อัตโนมัติทุกครั้งที่สั่งซื้อสินค้า
                  </p>
                )}
              </div>

              {/* Step 1: Add Friend */}
              <div className="border border-green-100 bg-green-50/40 p-4 rounded-2xl space-y-3">
                <div className="flex items-center justify-between gap-2 text-xs font-black text-green-900">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#06C755] text-white flex items-center justify-center text-[10px]">1</span>
                    <span>เพิ่มเพื่อน LINE Official Account</span>
                  </div>
                  <span className="text-[11px] font-mono font-bold text-emerald-700 bg-white px-2 py-0.5 rounded-md border border-emerald-200">
                    ID: @997wvsow
                  </span>
                </div>

                <div className="space-y-2.5">
                  <div className="flex justify-center py-1">
                    <a
                      href="https://lin.ee/PSM7qHqg"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block transition-transform hover:scale-[1.03] active:scale-95"
                    >
                      <img 
                        src="https://scdn.line-apps.com/n/line_add_friends/btn/th.png" 
                        alt="เพิ่มเพื่อน" 
                        height="36" 
                        style={{ border: 0, height: '36px' }} 
                      />
                    </a>
                  </div>

                  <div className="flex items-center gap-2 pt-1">
                    <div className="flex-1 bg-white border border-green-200 rounded-xl px-3 py-2 text-xs font-mono font-bold text-slate-700 flex items-center justify-between">
                      <span>CONNECT_{memberId}</span>
                      <button
                        type="button"
                        onClick={copyConnectCode}
                        className="text-xs text-teal-700 hover:text-teal-900 font-bold flex items-center gap-1 cursor-pointer"
                      >
                        {copiedCode ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                        <span>{copiedCode ? 'คัดลอกแล้ว' : 'คัดลอกรหัส'}</span>
                      </button>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500 leading-relaxed">
                    * เมื่อกดเพิ่มเพื่อนแล้ว ให้ส่งรหัส <code>CONNECT_{memberId}</code> หรือพิมพ์เฉพาะรหัสสมาชิก <code>{memberId}</code> ในช่องแชท LINE
                  </p>
                </div>
              </div>

              {/* Step 2: Check Status */}
              <div className="border border-slate-200 bg-white p-4 rounded-2xl flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs font-black text-slate-800">
                  <span className="w-5 h-5 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px]">2</span>
                  <span>ส่งข้อความในแชทแล้ว ตรวจสอบสถานะ:</span>
                </div>

                <button
                  type="button"
                  disabled={checkingLineStatus}
                  onClick={() => syncLineBindingStatus(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-50 hover:bg-teal-100 text-teal-700 text-xs font-bold rounded-xl border border-teal-200 transition-colors cursor-pointer disabled:opacity-50 shrink-0"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${checkingLineStatus ? 'animate-spin' : ''}`} />
                  <span>{checkingLineStatus ? 'กำลังตรวจ...' : 'ตรวจสอบสถานะ'}</span>
                </button>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end shrink-0">
              <button
                type="button"
                onClick={() => setIsLineModalOpen(false)}
                className="px-6 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold text-xs rounded-xl transition-colors cursor-pointer"
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sales Slip Receipt Modal */}
      <SalesSlipModal
        order={selectedOrderForSlip}
        distributor={distributor}
        isOpen={!!selectedOrderForSlip}
        onClose={() => setSelectedOrderForSlip(null)}
      />
    </div>
  );
};
